<template>
  <b-message v-if="error" type="is-danger" has-icon icon-size="is-small">
    <slot name="error">
      <h2>{{ $t('error') }}</h2>
      <p>{{ $t('unexpected-error-info-message') }}</p>
    </slot>
  </b-message>

  <div v-else class="cytomine-table-wrapper">
    <b-loading :is-full-page="false" :active="!data || loading" />
    <b-table
      v-if="data"
      :data="data"
      :loading="loading"
      :detailed="detailed"
      :detail-key="detailKey"
      :opened-detailed="openedDetailed.slice()"
      @update:openedDetailed="$emit('update:openedDetailed', $event)"
      backend-sorting
      :default-sort="sort"
      :default-sort-direction="order"
      :checkable="checkable"
      :checked-rows.sync="internalCheckedRows"
      :is-row-checkable="isRowCheckable"
      :sticky-header="true"
      :height="tableHeight"
      @sort="updateSort"
      ref="bTable" >
      <template #header="{column}">
        <slot name="header" :column="column" :index="index">
          {{ column.label }}
        </slot>
      </template>

      <template #default="{row, index}">
        <slot :row="row" :index="index" @click.native="toggleRowCheck(row)"></slot>
      </template>

      <template #detail="{row, index}">
        <slot name="detail" :row="row" :index="index"></slot>
      </template>

      <template #empty>
        <div class="content has-text-grey has-text-centered">
          <slot name="empty" :row="row" :index="index">
            {{$t('no-result')}}
          </slot>
        </div>
      </template>

      <template #footer>
        <slot name="footer"></slot>
      </template>
    </b-table>
  </div>
</template>

<script>
export default {
  name: 'cytomine-lazy-table',
  props: {
    collection: Object,
    isEmpty: { type: Boolean, default: false },
    perPage: { type: Number, default: 25 },
    detailed: { type: Boolean, default: true },
    detailKey: { type: String, default: 'id' },
    openedDetailed: { type: Array, default: () => [] },
    sort: { type: String },
    order: { type: String, default: 'asc' },
    revision: Number,
    refreshInterval: { type: Number, default: 0 },
    checkable: { type: Boolean, default: false },
    checkedRows: { type: Array, default: () => [] },
    isRowCheckable: { type: Function },
    tableHeight: { type: String, default: 'calc(100vh - 75px - 135px)' }
  },
  data() {
    return {
      index: 0,
      row: null,
      data: [],
      error: false,
      loading: false,
      total: 0,
      internalCheckedRows: [],
      internalCurrentPage: 1,
      internalSort: null,
      internalOrder: null,
      timeout: null,
      allDataLoaded: false,
      scrollElement: null, // 새로 추가: 스크롤 이벤트 리스너를 붙일 요소
    };
  },
  computed: {
    internalCollection() {
      let collection = this.collection.clone();
      // 'page' 대신 'offset'을 사용해야 할 수 있지만, fetchPage가 page(0부터 시작)를 받는다고 가정하고 max/sort/order 설정
      collection.max = this.perPage;
      collection.sort = this.internalSort;
      collection.order = this.internalOrder;
      return collection;
    }
  },
  watch: {
    revision() {
      this.resetAndFetch();
    },
    refreshInterval(value) {
      if (value === 0) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      else if (!this.timeout && value > 0) { // value > 0 조건 추가
        this.scheduleRefresh();
      }
    },
    internalCheckedRows: {
      handler(newVal) {
        this.$emit('update:checkedRows', newVal);
      },
      deep: true
    },
    checkedRows: {
      handler(newVal) {
        this.internalCheckedRows = newVal;
      },
      deep: true
    }
  },
  methods: {
    updateSort(sort, order) {
      this.internalSort = sort;
      this.internalOrder = order;
      this.$emit('update:sort', sort);
      this.$emit('update:order', order);
      this.resetAndFetch();
    },
    hasNoScroll(wrapper) {
      return wrapper.scrollHeight <= wrapper.clientHeight;
    },
    // async fetchPage() {
    //   // 데이터 로드 중이거나 모든 데이터가 로드된 경우 중복 요청 방지
    //   if (this.loading || this.allDataLoaded) return;

    //   this.loading = true;
    //   try {
    //     let data = { array: [], totalNbItems: 0 };
    //     // is-empty prop이 true이면 API 호출을 건너뜁니다.
    //     if (!this.isEmpty) {
    //       // internalCurrentPage는 1부터 시작, fetchPage는 0부터 시작하는 페이지 인덱스를 받으므로 -1
    //       data = await this.internalCollection.fetchPage(this.internalCurrentPage - 1);
    //     }

    //     if (data.array.length === 0) {
    //       this.allDataLoaded = true;
    //     }
    //     else {
    //       // 기존 데이터에 새로 불러온 데이터를 추가 (무한 스크롤 핵심)
    //       this.data.push(...data.array);
    //       this.total = data.totalNbItems;
    //       this.internalCurrentPage++;
    //     }

    //     // 선택된 row가 계속 존재하도록 업데이트
    //     this.internalCheckedRows = this.data.filter(member =>
    //       this.internalCheckedRows.map(u => u.id).includes(member.id)
    //     );

    //     this.$emit('update:checkedRows', this.internalCheckedRows);
    //   }
    //   catch (err) {
    //     console.error('Error fetching data:', err);
    //     this.error = true;
    //   }
    //   finally {
    //     this.loading = false;
    //     this.scheduleRefresh();
    //   }
      

    //   // ✅ 자동 로딩 로직
    //   this.$nextTick(() => {
    //     const wrapper = this.$el.querySelector('.table-wrapper');

    //     if (!wrapper) return;

    //     const noScroll = wrapper.scrollHeight <= wrapper.clientHeight;
    //     const notAllLoaded = this.data.length < this.total;

    //     if (noScroll && !notAllLoaded) {
    //       return; // 스크롤도 없고 전체도 다 가져왔으면 끝
    //     }

    //     if (noScroll && notAllLoaded) {
    //       // ✅ 스크롤 없고 완전히 다 안 가져왔으면 → 자동 fetch
    //       this.fetchPage();
    //     }
    //   });
    
    // },
    async fetchPage() {
      // 중복 요청 방지
      if (this.loading) return;

      // ✅ 더 가져올 데이터가 없으면 즉시 종료
      if (this.data.length >= this.total && this.total !== 0) {
        this.allDataLoaded = true;
        return;
      }

      this.loading = true;

      try {
        // currentPage는 1부터, fetchPage는 0부터 시작
        const pageIndex = this.internalCurrentPage - 1;

        let pageData = { array: [], totalNbItems: 0 };

        if (!this.isEmpty) {
          pageData = await this.internalCollection.fetchPage(pageIndex);
        }

        // total 업데이트
        this.total = pageData.totalNbItems;

        // ✅ 새 데이터 push
        if (pageData.array.length > 0) {
          this.data.push(...pageData.array);
          this.internalCurrentPage++;
        }

        // ✅ 선택된 체크는 계속 유지
        this.internalCheckedRows = this.data.filter(member =>
          this.internalCheckedRows.map(u => u.id).includes(member.id)
        );
        this.$emit('update:checkedRows', this.internalCheckedRows);

      } 
      catch (err) {
        console.error('Error fetching data:', err);
        this.error = true;
      } 
      finally {
        this.loading = false;
        this.scheduleRefresh();
      }
      this.$nextTick(() => {
        const wrapper = this.scrollElement;

        if (!wrapper) return;

        const noScroll = wrapper.scrollHeight <= wrapper.clientHeight;
        const notAllLoaded = this.data.length < this.total;

        if (noScroll && notAllLoaded) {
          this.fetchPage();
        }
      });
      
    },
    toggleRowCheck(row) {
      // 체크박스 기능이 활성화된 경우 클릭 시 선택/해제 로직을 추가할 수 있습니다.
      // 현재는 <b-table>의 checked-rows.sync가 체크박스를 통해 처리되므로 이 함수는 기본적으로 남겨두고,
      // slot에서 @click.native를 사용하고 있으므로, row 클릭 시 체크를 토글하려면 이 로직을 구현해야 합니다.
      if (!this.checkable) return;

      const index = this.internalCheckedRows.findIndex(r => r.id === row.id);
      if (index === -1) {
        this.internalCheckedRows.push(row);
      }
      else {
        this.internalCheckedRows.splice(index, 1);
      }
    },
    resetAndFetch() {
      this.data = [];
      this.internalCurrentPage = 1;
      this.allDataLoaded = false;
      this.fetchPage();
    },
    scheduleRefresh() {
      clearTimeout(this.timeout);
      if (this.refreshInterval && this.refreshInterval > 0) {
        this.timeout = setTimeout(this.fetchPage, this.refreshInterval);
      }
    },
    setupScrollListener() {
      const bTableComponent = this.$refs.bTable;
      if (!bTableComponent || !bTableComponent.$el) return;

      this.scrollElement = bTableComponent.$el.querySelector('.table-wrapper');

      if (this.scrollElement) {
        this.scrollElement.addEventListener('scroll', this.onScroll);
      }
      else {
        console.warn('Could not find BTable\'s internal scroll element (.table-wrapper).');
      }
    },
    removeScrollListener() {
      if (this.scrollElement) {
        this.scrollElement.removeEventListener('scroll', this.onScroll);
        this.scrollElement = null;
      }
    },
    onScroll() {
      if (!this.scrollElement || this.loading || this.allDataLoaded) return;

      const { scrollTop, clientHeight, scrollHeight } = this.scrollElement;

      // 스크롤이 끝에서 50px 이내에 도달했을 때
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        // if (this.data.length >= this.total) return;
        this.fetchPage();
      }
    }
  },
  created() {
    this.internalCurrentPage = 1;
    this.internalSort = this.sort;
    this.internalOrder = this.order;
    this.internalCheckedRows = this.checkedRows;
  },
  mounted() {
    // 정렬 초기화
    if (this.sort && this.order) {
      this.$nextTick(() => {
        this.updateSort(this.sort, this.order);
      });
    }

    // 스크롤 리스너 설정
    this.$nextTick(this.setupScrollListener);
    
    // 초기 데이터 로드
    this.fetchPage();
  },
  beforeDestroy() {
    this.removeScrollListener();
    clearTimeout(this.timeout);
  }
};
</script>

<style>
/* 기존 스타일은 그대로 유지 */
.cytomine-table-wrapper {
  min-height: 10em;
  position: relative;
  /* 스크롤은 BTable 내부에서 처리하므로 여기서 overflow-y: auto는 제거하거나,
     BTable height 속성 사용에 따라 조정해야 합니다.
     sticky-header와 height를 사용하면 BTable이 스크롤러를 만듭니다. */
}

.cytomine-table-wrapper table thead th {
  vertical-align: middle;
  font-weight: 600 !important;
  border: none !important;
  font-size: 0.9rem;
}

.cytomine-table-wrapper table tbody td {
  vertical-align: middle;
}

/* 테이블의 thead 양 끝에만 둥근 모서리 별도 지정 */
.cytomine-table-wrapper table thead tr th:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
.cytomine-table-wrapper table thead tr th:last-child {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

/* sticky header 디자인 적용 */
.b-table .table-wrapper.has-sticky-header tr th {
  background-color: #F4F4F4 !important;
  box-shadow: none;
  height: 58px;
}

.cytomine-table-wrapper .is-checked {
  background-color: #E6F5FC;
  font-weight: 700;
}

.table-wrapper {
  height: 100%;
}
</style>
